import { useEffect, useMemo, useReducer } from 'react';
import { container } from '@/container/CompositionRoot';
import { GlobalFilterController } from './GlobalFilterController';
import type { GlobalFilterSnapshot } from './GlobalFilterTypes';

/**
 * Resolves the shared {@link GlobalFilterController} from the root container.
 * Always the same instance regardless of route sub-container, and never
 * disposed on unmount — it outlives every page.
 */
export function useGlobalFilterController(): GlobalFilterController {
    return container.resolve(GlobalFilterController);
}

/**
 * Subscribes to the global filter controller and returns a fresh snapshot on
 * each change. Use in components that render or query off the global filters.
 */
export function useGlobalFilters(): { controller: GlobalFilterController; snapshot: GlobalFilterSnapshot } {
    const controller = useGlobalFilterController();
    const [, forceUpdate] = useReducer((count: number) => count + 1, 0);
    useEffect(() => controller.subscribe(() => forceUpdate()), [controller]);
    return { controller, snapshot: controller.snapshot() };
}

/**
 * A stable-valued string identifying the current global filter state. Include it
 * in a data view's load-cache key so a global filter change (date/provider/person)
 * busts the cache and re-queries — content-based filter dedupe alone won't, since
 * the global tier lives outside the page's local filter array.
 */
export function useGlobalFilterKey(): string {
    const { snapshot } = useGlobalFilters();
    return useMemo(() => JSON.stringify(snapshot), [snapshot]);
}
